/**
 * SolverTable — 문제 상세 [학생] 탭의 여덟 칸 표(설계 `docs/problem-hub-v2/0911_SPEC_problem-student-tab.html` §4).
 *
 * 순수 표시다 — 불러오기·context 를 갖지 않는다(PaperTable 과 같은 성격).
 *
 * 틀은 시험지 표(`ExamPapersV2/PaperTable.jsx`)의 값을 그대로 옮겼다 — 둥근 테두리·옅은 회색 머리 칸·
 * 옅은 가로줄 행·빈 목록 안내. 같은 상세 창의 두 목록 탭([시험지]·[학생])이 다른 얼굴이면
 * 같은 종류의 목록으로 읽히지 않는다. PaperTable 을 그대로 쓰지 않은 이유는 칸이 전부 달라서다 — 그 표는
 * 시험지 필드(폴더·작성자·판)와 드래그·이동에 묶여 있다.
 *
 * 번호 칸만은 시험지 표의 고정폭 글자가 아니라 **학생 번호의 얼굴**(동그란 초록 번호)이다 — 학생관리 표와
 * 같은 값(오너 지시 2026-09-11). 시험지 번호와 학생 번호는 다른 것이라 같은 글자로 두면 같은 종류로 읽힌다.
 *
 * 학생 칸의 글자는 「전체 테스트」 표(StudentsHub/AssignmentFeedTable)와 같은 도우미를 쓴다 — 같은 학생이
 * 화면마다 다른 글자로 보이면 같은 사람인 줄 모른다.
 *
 * 행을 누르면 그 학생의 학습 화면으로 떠나므로 「고른 행」 표시(진한 초록·왼쪽 띠 색)는 두지 않는다 —
 * 선택이 머물 일이 없다. 왼쪽 3px 투명 띠는 남긴다: 시험지 표와 첫 칸의 자리가 같아야 한다.
 *
 * 여덟 칸 모두 머리를 누르면 줄이 선다(내림차순 → 오름차순 → 기본, common/tableSort). 목록을 통째로
 * 받는 표라 브라우저 안에서 세워도 전체 차례다. 기본은 서버가 준 차례 — 마지막 출제 최근순이다(solversApi).
 */
import { useMemo, useState } from 'react';
import { studentSchoolText, studentClassText, fmtServerDateTime } from '../ExamPapersV2/examPaperApi';
import SortableTh from '../common/SortableTh';
import { nextSort, sortRows, textSortValue, timeSortValue } from '../common/tableSort';
import { SOLVER_RESULT, solverResultOf } from './solversApi';

/**
 * 폭 — 시험지 표의 같은 성격 칸과 맞춘다(번호 7% · 수 8% · 날짜 12%). 남는 폭은 학생 글자 칸이 나눠 쓴다.
 * 머리와 몸이 같은 배열을 읽는다 — 갈리면 표가 통째로 어긋난다.
 */
const COLUMNS = [
  { key: 'id', label: '#ID', className: 'w-[7%] min-w-14' },
  { key: 'name', label: '학생', className: 'w-[13%] min-w-20' },
  { key: 'school', label: '학교·학년', className: 'w-[14%] min-w-24' },
  { key: 'klass', label: '담당·강의', className: 'w-auto min-w-32' },
  { key: 'result', label: '최근 결과', className: 'w-[10%] min-w-16' },
  { key: 'assigned', label: '출제', className: 'w-[8%] min-w-14' },
  { key: 'attempts', label: '채점', className: 'w-[8%] min-w-14' },
  { key: 'lastAssigned', label: '마지막 출제', className: 'w-[12%] min-w-28' },
];

/** 값이 안 온 칸 — 시험지 표와 같은 흐린 줄표 */
const NONE = <span className="text-gray-300">—</span>;
/** 학교·강의가 비어 있는 칸 — 「전체 테스트」 표와 같은 짧은 줄표(AssignmentFeedTable 은 삭제 학생 신상에 '-' 를 쓴다) */
const BLANK = <span className="text-gray-300">-</span>;

/** 정수면 그 값, 아니면 null — 「0회」와 「값이 안 옴」을 뭉개지 않는다 */
function countOf(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
}

/** 학생 이름 — 비어 있으면 null(칸에는 「(이름 없음)」) */
function nameOf(it) {
  return typeof it?.student_name === 'string' && it.student_name.trim() !== '' ? it.student_name : null;
}

/**
 * 최근 결과의 정렬 차례 — 정답 > 오답 > 무응답 > 채점 전. 내림차순이면 맞힌 학생이 위로 온다.
 * 「채점 전」은 값이 없는 칸이 아니라 한 갈래라 맨 아래로 빼지 않고 차례에 넣는다.
 */
const RESULT_RANK = { correct: 3, wrong: 2, unanswered: 1, ungraded: 0 };

/** 칸마다 **그린 값**으로 세운다 — 글자 칸은 화면 글자, 수 칸은 수, 날짜는 시각 */
function solverSortValue(it, key) {
  switch (key) {
    case 'id': return countOf(it?.student_id);
    case 'name': return nameOf(it);
    case 'school': return textSortValue(studentSchoolText(it));
    case 'klass': return textSortValue(studentClassText(it));
    case 'result': return RESULT_RANK[solverResultOf(it)] ?? null;
    case 'assigned': return countOf(it?.assigned_count);
    case 'attempts': return countOf(it?.attempts);
    case 'lastAssigned': return timeSortValue(it?.last_assigned_at);
    default: return null;
  }
}

/** 출제 칸의 풀이 — 0회는 출제 기록 없이 채점만 남은 학생이다(시험이 지워졌거나 옛 기록, 회신 §1) */
function assignedTitle(it, n) {
  if (n == null) return undefined;
  if (n === 0) return '출제 기록 없이 채점만 남았습니다 — 시험이 지워졌거나 옛 기록입니다';
  return `레거시 시험 ${countOf(it?.exam_count) ?? 0}회 · 새 시험지 ${countOf(it?.assignment_count) ?? 0}회`;
}

function SolverTable({ items = [], empty = null, onSelect = null }) {
  const cell = 'px-3 py-2';
  // 정렬은 이 표 안에서만 산다 — [새로고침]으로 목록이 다시 와도 고른 칸은 남고, 문제가 바뀌면 탭째 새로 선다
  const [sort, setSort] = useState(null);
  const rows = useMemo(() => sortRows(items, sort, solverSortValue), [items, sort]);
  const onSort = (key) => setSort((prev) => nextSort(prev, key));
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {COLUMNS.map((c) => (
              <SortableTh
                key={c.key}
                sortKey={c.key}
                sort={sort}
                onSort={onSort}
                className={`whitespace-nowrap ${cell} text-center text-xs font-medium tracking-wider text-gray-500 ${c.className}`}
              >
                {c.label}
              </SortableTh>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {/* 0명이어도 표를 걷지 않는다 — 머리가 사라지면 「아직 안 왔나」와 「없나」가 같은 화면이 된다(PaperTable 과 같은 규칙) */}
          {rows.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="px-3 py-10 text-center">{empty}</td>
            </tr>
          )}
          {rows.map((it, i) => {
            const sid = it?.student_id ?? null;
            const name = nameOf(it);
            const school = studentSchoolText(it);
            const klass = studentClassText(it);
            const result = SOLVER_RESULT[solverResultOf(it)] || null;
            const assigned = countOf(it?.assigned_count);
            const attempts = countOf(it?.attempts);
            const when = fmtServerDateTime(it?.last_assigned_at);
            const open = onSelect && sid != null ? () => onSelect(sid) : undefined;
            return (
              <tr
                key={sid ?? `row-${i}`}
                onClick={open}
                className={`border-b border-gray-100 transition-colors duration-150 last:border-b-0 ${
                  open ? 'cursor-pointer hover:bg-primary/10' : ''
                }`}
              >
                {/* 번호는 학생관리 표의 동그란 번호와 **같은 얼굴**이다(오너 지시 2026-09-11) — 학습 화면의
                    「전체 테스트」 표(AssignmentFeedTable)와 같은 클래스·같은 title·값이 없을 때 '-'. */}
                <td className={`whitespace-nowrap border-l-[3px] border-transparent ${cell}`}>
                  <div
                    className="flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white"
                    title={sid != null ? `학생 ID: ${sid}` : '학생 정보 없음'}
                  >
                    {sid ?? '-'}
                  </div>
                </td>
                <td className={cell}>
                  <span className="line-clamp-1 font-medium text-gray-900" title={name || ''}>
                    {name || <span className="font-normal text-gray-300">(이름 없음)</span>}
                  </span>
                </td>
                <td className={`whitespace-nowrap ${cell} text-gray-600`}>{school || BLANK}</td>
                <td className={`whitespace-nowrap ${cell} text-gray-600`}>{klass || BLANK}</td>
                <td className={`whitespace-nowrap ${cell} text-center`}>
                  {result ? <span className={result.cls}>{result.label}</span> : NONE}
                </td>
                <td
                  className={`whitespace-nowrap ${cell} text-right ${assigned === 0 ? 'text-gray-400' : 'text-gray-900'}`}
                  title={assignedTitle(it, assigned)}
                >
                  {assigned == null ? NONE : `${assigned}회`}
                </td>
                <td className={`whitespace-nowrap ${cell} text-right ${attempts === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                  {attempts == null ? NONE : `${attempts}회`}
                </td>
                <td className={`whitespace-nowrap ${cell} text-xs text-gray-500`}>{when || NONE}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default SolverTable;
